import { Info, Keyboard } from 'lucide-react';
import { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { Kbd } from '@/components/ui/kbd';
import {
  SHORTCUT_GROUPS,
  type Shortcut,
  isMacPlatform,
  renderKeyToken,
} from '@/features/settings/shortcuts-catalog';

function ShortcutRow({ item, mac }: { item: Shortcut; mac: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5 px-4 border-b border-border last:border-b-0">
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-sm text-fg">{item.name}</span>
        {item.desc && <span className="text-xs text-fg-muted">{item.desc}</span>}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {item.keys.map((k, i) => (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && <span aria-hidden="true" className="text-[10px] text-fg-subtle">+</span>}
            <Kbd>{renderKeyToken(k, mac)}</Kbd>
          </span>
        ))}
      </div>
    </div>
  );
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ShortcutsSheet({ open, onOpenChange }: Props) {
  const mac = useMemo(() => isMacPlatform(), []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <header className="flex items-center gap-2 px-4 h-12 border-b border-border shrink-0">
          <Keyboard size={16} strokeWidth={1.75} className="text-fg-muted" aria-hidden="true" />
          <DialogTitle>Atalhos de teclado</DialogTitle>
          <span className="ml-2 inline-flex items-center h-5 px-2 rounded-md bg-primary/15 text-primary text-[10px] font-medium uppercase tracking-wide">
            {mac ? 'macOS' : 'Win/Linux'}
          </span>
        </header>

        <DialogDescription className="sr-only">
          Lista completa de atalhos de teclado disponíveis no painel.
        </DialogDescription>

        <div className="flex-1 overflow-y-auto">
          {SHORTCUT_GROUPS.map((group) => (
            <div key={group.label}>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-fg-subtle px-4 pt-4 pb-2">
                {group.label}
              </div>
              <div>
                {group.items.map((item) => (
                  <ShortcutRow key={item.name} item={item} mac={mac} />
                ))}
              </div>
            </div>
          ))}
        </div>

        <footer className="flex items-center gap-2 px-4 h-11 border-t border-border text-xs text-fg-muted shrink-0">
          <Info size={12} strokeWidth={1.75} className="text-fg-subtle" aria-hidden="true" />
          <span>Combinações com modificador funcionam mesmo quando você está digitando.</span>
          <div className="ml-auto flex items-center gap-1.5">
            <Kbd>Esc</Kbd>
            <span>fechar</span>
          </div>
        </footer>
      </DialogContent>
    </Dialog>
  );
}
